import type { MouseEvent, PointerEvent, ReactNode, Ref } from 'react'
import cx from 'classnames'
import { RawButton } from '@/components/Button'
import { SearchField } from '@/components/SearchField'
import { Skeleton, useSkeletonListContext } from '@/components/Skeleton'
import styles from './TableView.module.css'

export interface TableViewProps {
  children?: ReactNode
}

/**
 * A full-height page around a data table: a toolbar on top and a scrolling
 * area below.
 */
export function TableView({ children }: TableViewProps) {
  return <div className={styles.root}>{children}</div>
}

export interface TableToolbarProps {
  children?: ReactNode
}

/**
 * The row above the table holding the tabs and actions.
 */
export function TableToolbar({ children }: TableToolbarProps) {
  return <div className={styles.toolbar}>{children}</div>
}

export interface TableTabsProps {
  children?: ReactNode
  'aria-label'?: string
}

/**
 * Tabs that switch the table between views.
 */
export function TableTabs({
  children,
  'aria-label': ariaLabel,
}: TableTabsProps) {
  return (
    <div className={styles.tabs} role="tablist" aria-label={ariaLabel}>
      {children}
    </div>
  )
}

export interface TableTabProps {
  children?: ReactNode
  isSelected: boolean
  onPress: () => unknown
  /**
   * Shown inside the tab after its label, such as a button for the tab's
   * menu.
   */
  trailing?: ReactNode
  /**
   * Marks a tab that isn't kept, such as one opened from a shared link.
   */
  isDashed?: boolean
  onContextMenu?: (e: MouseEvent<HTMLElement>) => void
  onPointerDown?: (e: PointerEvent<HTMLElement>) => void
  className?: string
  ref?: Ref<HTMLElement>
  testId?: string
}

/**
 * A tab in {@link TableTabs}.
 */
export function TableTab({
  children,
  isSelected,
  onPress,
  trailing,
  isDashed,
  onContextMenu,
  onPointerDown,
  className,
  ref,
  testId,
}: TableTabProps) {
  const tabClassName = cx(
    styles.tab,
    isSelected && styles.tabSelected,
    isDashed && styles.tabDashed,
    className,
  )

  if (!trailing) {
    return (
      <RawButton
        ref={ref as Ref<HTMLButtonElement>}
        role="tab"
        aria-selected={isSelected}
        className={tabClassName}
        onPress={onPress}
        onContextMenu={onContextMenu}
        onPointerDown={onPointerDown}
        testId={testId}
      >
        {children}
      </RawButton>
    )
  }

  return (
    <span
      ref={ref as Ref<HTMLSpanElement>}
      className={cx(tabClassName, styles.tabWithTrailing)}
      onContextMenu={onContextMenu}
      onPointerDown={onPointerDown}
    >
      <RawButton
        role="tab"
        aria-selected={isSelected}
        className={styles.tabLabel}
        onPress={onPress}
        testId={testId}
      >
        {children}
      </RawButton>
      {trailing}
    </span>
  )
}

/**
 * A placeholder for a {@link TableTab} still loading. Render it inside a
 * `SkeletonList`, which varies its width per tab.
 */
export function TableTabSkeleton() {
  const { vary } = useSkeletonListContext()
  return (
    <Skeleton
      variant="rect"
      width={vary(88)}
      height="var(--control-sm)"
      borderRadius="var(--rounded-md)"
    />
  )
}

export interface TableActionsProps {
  children?: ReactNode
}

/**
 * The toolbar's trailing group of controls.
 */
export function TableActions({ children }: TableActionsProps) {
  return <div className={styles.actions}>{children}</div>
}

export interface TableSearchProps {
  value: string
  onChange: (value: string) => unknown
  placeholder: string
  testId?: string
}

/**
 * The toolbar's search field.
 */
export function TableSearch({
  value,
  onChange,
  placeholder,
  testId,
}: TableSearchProps) {
  return (
    <div className={styles.search}>
      <SearchField
        size="small"
        stretch
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        testId={testId}
      />
    </div>
  )
}

export interface TableScrollProps {
  children?: ReactNode
}

/**
 * The scrolling area holding the table.
 */
export function TableScroll({ children }: TableScrollProps) {
  return <div className={styles.scroll}>{children}</div>
}

export interface TableEmptyViewProps {
  children?: ReactNode
}

/**
 * Spaces out the empty state shown instead of the table.
 */
export function TableEmptyView({ children }: TableEmptyViewProps) {
  return <div className={styles.emptyView}>{children}</div>
}
